import { MathUtils, Matrix4, Object3D, Vector3 } from 'three';

export interface UcsDisplayConfig {
  enabled: boolean;
  origin?: number[];
  rotation?: number[];
}

/**
 * The turn the run gives the model for a user coordinate system: about world
 * X, then Y, then Z, around the UCS origin. The editor never shows it; content
 * in the UCS frame is placed with its inverse. Identity when the UCS is off.
 */
export function ucsDisplayMatrix(ucs: UcsDisplayConfig | null | undefined): Matrix4 {
  if (!ucs?.enabled) return new Matrix4();

  const [x = 0, y = 0, z = 0] = ucs.rotation ?? [];
  const turn = new Object3D();
  turn.rotateOnWorldAxis(new Vector3(1, 0, 0), MathUtils.degToRad(x));
  turn.rotateOnWorldAxis(new Vector3(0, 1, 0), MathUtils.degToRad(y));
  turn.rotateOnWorldAxis(new Vector3(0, 0, 1), MathUtils.degToRad(z));
  turn.updateMatrix();

  const [ox = 0, oy = 0, oz = 0] = ucs.origin ?? [];
  return new Matrix4()
    .makeTranslation(ox, oy, oz)
    .multiply(turn.matrix)
    .multiply(new Matrix4().makeTranslation(-ox, -oy, -oz));
}

/** The inverse of `ucsDisplayMatrix`: the UCS frame back to the customer's own. Identity when the UCS is off. */
export function ucsUndoMatrix(ucs: UcsDisplayConfig | null | undefined): Matrix4 {
  return ucs?.enabled ? ucsDisplayMatrix(ucs).invert() : new Matrix4();
}

/** Gives `object` exactly `matrix` as its local transform. */
export function placeObject(object: Object3D, matrix: Matrix4): void {
  matrix.decompose(object.position, object.quaternion, object.scale);
  object.updateMatrix();
}
